import { AnalyticsEventName } from '@dupe/common/constants'
import { motion } from 'framer-motion'
import { AnimatePresence } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import LoaderIcon from '~/src/components/svg/LoaderIcon'
import { DrawerContent, DrawerTrigger } from '~/src/components/ui/drawer'
import { Drawer } from '~/src/components/ui/drawer'
import useAnalytics from '~/src/hooks/useAnalytics'
import useCaptureUTMParams from '~/src/hooks/useCaptureUTMParams'
import useDeviceDetect from '~/src/hooks/useDeviceDetect'
import useFetchDisoverFeed, { Item } from '~/src/hooks/useFetchDisoverFeed'
import { cn } from '~/src/lib/utils'

// Simple seeded PRNG (Mulberry32)
// This ensures the randomization is consistent within the same hour
const seededRandomGenerator = (seed: number) => {
  return function () {
    let t = (seed += 0x6d2b79f5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// New DiscoveryItem component
interface DiscoveryItemProps {
  item: Item
  navigatingTo: string | undefined
  onClick: () => void
}

const DiscoveryItem: React.FC<DiscoveryItemProps> = ({
  item,
  navigatingTo,
  onClick,
}) => {
  const analytics = useAnalytics()

  return (
    <Link
      key={item.id}
      className='relative break-inside-avoid overflow-hidden rounded-lg'
      href={`/results/${item.hash}?ref=home-discovery-drawer`}
      onClick={() => {
        onClick()

        analytics.track(
          AnalyticsEventName.NoURLResultClick,
          {
            ...item,
          },
          {
            sendToPlausible: true,
            sendToFb: true,
          }
        )
      }}
    >
      <Image
        src={`https://assets.dupe.com/${encodeURIComponent(item.image)}?tr=w-300`}
        alt={item.image}
        width={item.width || 300}
        height={item.height || 300}
        unoptimized
        onContextMenu={(e) => e.preventDefault()}
        className='w-full rounded object-cover md:rounded-lg'
        style={{
          aspectRatio: `${item.width} / ${item.height}`,
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
        draggable={false}
      />
      <div
        className={cn('pointer-events-none absolute inset-0', {
          'bg-black/50': navigatingTo === item.hash,
          'bg-black/10': navigatingTo !== item.hash,
        })}
      />
      {navigatingTo === item.hash && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className='pointer-events-none absolute inset-0 flex items-center justify-center'
        >
          <div className='animate-spin'>
            <LoaderIcon height={50} className='text-white' />
          </div>
        </motion.div>
      )}
    </Link>
  )
}

const HomeDiscoveryDrawer = () => {
  const analytics = useAnalytics()
  const { isInstagram, loading } = useDeviceDetect()
  const utmParams = useCaptureUTMParams()
  const [open, setOpen] = useState(false)
  const [navigatingTo, setNavigatingTo] = useState<string>()

  const { data } = useFetchDisoverFeed({ limit: 100, enabled: isInstagram })

  const randomizedData = useMemo(() => {
    const currentHour = new Date().getHours()
    const array = (data || []).slice() // Create a copy to avoid mutating original data

    const random = seededRandomGenerator(currentHour) // Seed with the current hour

    // Fisher-Yates (Knuth) Shuffle using the seeded PRNG
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1))
      if (array[i] && array[j]) {
        const temp = array[i]!
        array[i] = array[j]!
        array[j] = temp
      }
    }

    return array
  }, [data])

  const column1Data = useMemo(() => {
    const midPoint = Math.ceil(randomizedData.length / 2)
    return randomizedData.slice(0, midPoint)
  }, [randomizedData])

  const column2Data = useMemo(() => {
    const midPoint = Math.ceil(randomizedData.length / 2)
    return randomizedData.slice(midPoint)
  }, [randomizedData])

  useEffect(() => {
    if (!isInstagram) return
    analytics.track(AnalyticsEventName.NoURLCTAShown)

    // The `analytics` object causes this useEffect to
    // run multiple times, so we exclude it from the dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInstagram])

  if (loading) return null
  if (!isInstagram) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className='md:hidden'
      >
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerTrigger asChild>
            <button
              className='relative -top-0.5 text-xs font-semibold text-blue-300 underline'
              onClick={() => {
                analytics.track(
                  AnalyticsEventName.NoURLCTAClick,
                  {
                    ...utmParams,
                  },
                  {
                    sendToPlausible: true,
                    sendToFb: true,
                  }
                )
              }}
            >
              I don&apos;t have a URL
            </button>
          </DrawerTrigger>

          <DrawerContent className='fixed bottom-0 left-0 right-0 flex h-[90dvh] flex-col gap-4 px-4 pt-8 outline-none'>
            <div>
              <h2 className='font-semibold'>No URL? No problem.</h2>
              <p className='max-w-xs text-sm opacity-60'>
                Try duping one of these trending items that our community of
                users has found.
              </p>
            </div>
            <div className='no-scrollbar h-full flex-1 overflow-y-auto rounded-lg pb-8'>
              <div className='grid grid-cols-2 gap-4'>
                {/* Column 1 */}
                <div className='flex flex-col gap-4'>
                  {column1Data.map((item) => (
                    <DiscoveryItem
                      key={item.id}
                      item={item}
                      navigatingTo={navigatingTo}
                      onClick={() => setNavigatingTo(item.hash)}
                    />
                  ))}
                </div>
                {/* Column 2 */}
                <div className='flex flex-col gap-4'>
                  {column2Data.map((item) => (
                    <DiscoveryItem
                      key={item.id}
                      item={item}
                      navigatingTo={navigatingTo}
                      onClick={() => setNavigatingTo(item.hash)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      </motion.div>
    </AnimatePresence>
  )
}

export default HomeDiscoveryDrawer
